import React from 'react';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WeeklySlot, weeklySlotApiService } from '../../services/weeklySlotApi';

interface TimeSlotSelectorProps {
  selectedDate: string;
  selectedTime: string;
  selectedType: string;
  weeklySlots: WeeklySlot[];
  loadingSlots: boolean;
  isAuthenticated: boolean;
  onTimeSelect: (time: string) => void;
  onTypeSelect: (type: string) => void;
}

const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  selectedDate,
  selectedTime,
  selectedType,
  weeklySlots,
  loadingSlots,
  isAuthenticated,
  onTimeSelect,
  onTypeSelect
}) => {
  const navigate = useNavigate();

  const getAvailableSlotsForDate = (dateString: string): WeeklySlot[] => {
    if (!dateString || weeklySlots.length === 0) {
      return [];
    }
    
    const date = new Date(dateString);
    const jsWeekday = date.getDay();
    const weekday = jsWeekday === 0 ? 6 : jsWeekday - 1;
    
    const daySlots = weeklySlots.filter(slot => slot.weekday === weekday && slot.is_active);
    return daySlots.sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const getAvailableTimeSlotsForDate = (dateString: string): string[] => {
    const slots = getAvailableSlotsForDate(dateString);
    return slots.map(slot => weeklySlotApiService.formatTime(slot.start_time));
  };

  const getAppointmentTypesForTimeSlot = (timeSlot: string): string[] => {
    const slots = getAvailableSlotsForDate(selectedDate);
    const slot = slots.find(s => weeklySlotApiService.formatTime(s.start_time) === timeSlot);
    
    if (!slot) return [];
    
    switch (slot.appointment_type) {
      case 'online':
        return ['online'];
      case 'physical':
        return ['physical'];
      case 'both':
        return ['online', 'physical'];
      default:
        return [];
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Choisir un créneau</h2>
      
      {!selectedDate ? (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Sélectionnez d'abord une date</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Créneaux disponibles</h3>
            {loadingSlots ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Chargement des créneaux...</p>
              </div>
            ) : weeklySlots.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 mb-2">Aucun créneau configuré</p>
                <p className="text-xs text-gray-400">Contactez l'administrateur</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {getAvailableTimeSlotsForDate(selectedDate).length > 0 ? (
                  getAvailableTimeSlotsForDate(selectedDate).map((time: string) => (
                    <button
                      key={time}
                      onClick={() => onTimeSelect(time)}
                      className={`
                        p-3 text-sm rounded-lg border transition-all duration-200
                        ${selectedTime === time 
                          ? 'bg-blue-600 text-white border-blue-600' 
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }
                      `}
                    >
                      {time}
                    </button>
                  ))
                ) : (
                  <div className="col-span-3 text-center py-8">
                    <Clock className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">Aucun créneau disponible pour cette date</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {selectedTime && (
            <div className="space-y-3">
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Créneau sélectionné :</strong> {selectedTime}
                </p>
              </div>
              
              {/* Sélection du type de rendez-vous */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Type de rendez-vous</h4>
                <div className="grid grid-cols-1 gap-2">
                  {getAppointmentTypesForTimeSlot(selectedTime).map((type) => (
                    <button
                      key={type}
                      onClick={() => onTypeSelect(type)}
                      className={`
                        p-3 text-sm rounded-lg border transition-all duration-200 text-left
                        ${selectedType === type 
                          ? 'bg-blue-600 text-white border-blue-600' 
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }
                      `}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          {type === 'online' ? (
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <span className="text-green-600 text-sm">💻</span>
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-600 text-sm">🏢</span>
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium">
                            {type === 'online' ? 'Rendez-vous en ligne' : 'Rendez-vous physique'}
                          </p>
                          <p className="text-xs opacity-75">
                            {type === 'online' 
                              ? 'Visioconférence via Google Meet ou Zoom' 
                              : 'Dans nos locaux ou chez vous'
                            }
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Message pour utilisateurs non connectés */}
      {!isAuthenticated && (
        <div className="mt-6 mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-yellow-800">
              Veuillez vous connecter{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-blue-600 font-semibold underline hover:text-blue-800 transition-colors"
              >
                ici
              </button>
              {' '}pour prendre un rendez-vous.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeSlotSelector;
